/**
 * Domain Type Definitions for Project Kapital Platform
 * Conforms to contracts/app-data.contract.ts
 */

export type Locale = 'ko' | 'en';

export interface LocalizedText {
  /** Korean is the primary default language and must always be provided */
  ko: string;
  /** Optional English translation; falls back to Korean if omitted */
  en?: string;
}

export type PlatformType = 'ios' | 'android' | 'mac' | 'windows';

export const PLATFORM_LABELS: Record<PlatformType, string> = {
  android: 'Android',
  ios: 'iOS',
  mac: 'macOS',
  windows: 'Windows',
};

export type FaqCategory =
  | 'general'
  | 'account'
  | 'troubleshooting'
  | 'data-privacy'
  | 'payment';

export interface AppFeature {
  id: string;
  title: LocalizedText;
  description: LocalizedText;
  screenshotUrl?: string;
  badge?: LocalizedText;
}

export interface AppScreenshot {
  id: string;
  url: string;
  caption?: LocalizedText;
  alt: LocalizedText;
}

export interface FaqItem {
  id: string;
  category: FaqCategory;
  question: LocalizedText;
  answer: LocalizedText;
}

export interface AppConfig {
  id: string;
  slug: string;
  name: string;
  tagline: LocalizedText;
  description: LocalizedText;
  iconUrl: string;
  accentColor?: string; // Optional custom brand color; falls back to #3CAC81
  category: LocalizedText;
  platforms: PlatformType[];
  appStoreUrl?: string;
  googlePlayUrl?: string;
  currentVersion: string;
  lastUpdated: string; // ISO format: YYYY-MM-DD
  supportEmail: string;
  targetAudience?: LocalizedText[];
  features: AppFeature[];
  screenshots?: AppScreenshot[];
  faqs?: FaqItem[];
  privacyUrl?: string;
  termsUrl?: string;
}

export interface SupportCategory {
  id: string;
  iconName: string;
  title: LocalizedText;
  description: LocalizedText;
  faqCategoryId: FaqCategory;
}

export interface LegalSection {
  heading: LocalizedText;
  content: LocalizedText[];
}

export interface LegalDocument {
  id: 'privacy' | 'terms';
  title: LocalizedText;
  effectiveDate: string; // ISO format: YYYY-MM-DD
  sections: LegalSection[];
}
